import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useHttpClient } from '@bonusly/http-client';

import { bizyConversationPath } from './bizy-conversations';

// Path helpers
export const bizyChatV2Path = (prefix = '') => `${prefix}/bizy/chat/v2`;
export const bizyFeedbackPath = (messageId: string, prefix = '') => `${prefix}/bizy/feedback/${messageId}`;
export const bizyTitlePath = (prefix = '') => `${prefix}/bizy/title`;

// Streaming API Types
export interface BizyChatV2Request {
	message: string;
	conversation_id?: string;
	context?: Record<string, string>;
}

export interface BizyChatV2Response {
	success: boolean;
	conversation_id: string;
}

/**
 * Hook for sending chat messages to Bizy (streaming).
 *
 * POSTs to /api/v2/bizy/chat/v2 which enqueues a background job and returns
 * 202 immediately. The response is streamed via ActionCable (BizyChannel).
 */
export const useBizyChatV2Mutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyChatV2Request): Promise<BizyChatV2Response> => {
			const response = await http.post<BizyChatV2Response>(bizyChatV2Path(), params);
			return response.data;
		},
	});
};

// Feedback API Types
export interface BizyFeedbackRequest {
	messageId: string;
	feedback_type: 'positive' | 'negative' | null;
	feedback_category?: string;
	feedback_details?: string;
}

export interface BizyFeedbackResponse {
	success: boolean;
	error?: string;
}

/**
 * Hook for submitting feedback on a Bizy response.
 *
 * PATCH /api/v2/bizy/feedback/:id
 */
export const useBizyFeedbackMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async ({ messageId, ...data }: BizyFeedbackRequest): Promise<BizyFeedbackResponse> => {
			const response = await http.patch<BizyFeedbackResponse>(bizyFeedbackPath(messageId), data);
			return response.data;
		},
	});
};

// Title Generation API Types
export interface BizyTitleRequest {
	message: string;
}

export interface BizyTitleResponse {
	title: string;
}

/**
 * Hook for generating an AI conversation title from a user message.
 *
 * POST /api/v2/bizy/title — synchronous, no streaming, no persistence.
 */
export const useBizyTitleMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyTitleRequest): Promise<BizyTitleResponse> => {
			const response = await http.post<BizyTitleResponse>(bizyTitlePath(), params);
			return response.data;
		},
	});
};

// Stream Recovery Types
export interface BizyConversationMessage {
	id: string;
	user_message: string;
	bizy_response: string;
	emotion?: string;
	created_at: string;
}

interface BizyConversationResponse {
	conversation: {
		conversation_id: string;
		messages: BizyConversationMessage[];
	};
}

/**
 * Returns a function that fetches the last completed response for a conversation.
 * Used for stream recovery when ActionCable events are lost (killed worker, dropped connection).
 *
 * Returns null if the conversation doesn't exist, has no messages, or the endpoint
 * is unavailable (feature-flagged behind bizy_conversation_history).
 */
export const useBizyStreamRecovery = () => {
	const http = useHttpClient();

	return useCallback(
		async (conversationId: string): Promise<BizyConversationMessage | null> => {
			try {
				const response = await http.get<BizyConversationResponse>(bizyConversationPath(conversationId));
				const messages = response.data.conversation?.messages;
				if (messages?.length) {
					return messages[messages.length - 1];
				}
				return null;
			} catch {
				return null;
			}
		},
		[http]
	);
};
